/**
 * Registry-ul tipizat de setări (spec-arhitectura-backend §5, spec-admin §2).
 *
 * Sursa de adevăr pentru cheie, tip, default, descriere, categorie și validare.
 * `SettingsService` (apps/api) citește definițiile de aici, stochează DOAR valorile
 * suprascrise în Postgres (tabel `settings`), cu cache Redis + invalidare pub/sub.
 * Secretele NU trec pe aici — merg prin `SecretsService` (AES-256-GCM, write-only).
 *
 * Adminul afișează setările generic, grupate pe categorii (tab-uri), cu descriere +
 * default vizibil (spec-admin: Limite generare / Retenție fișiere / Cost control /
 * Aplicație / Cache Redis).
 */
import { DEFAULT_CATALOG_BADGES, DEFAULT_MODELS_BY_PROVIDER, DEFAULT_MUSIC_MOODS, DEFAULT_PROVIDERS_BY_STEP, DEFAULT_TEMPLATE_NICHES, DEFAULT_VOICE_STYLES, } from './options.js';
/** Categoriile (tab-urile) sub care adminul grupează setările. */
export const SETTING_CATEGORIES = {
    limite_generare: 'Limite generare',
    retentie_fisiere: 'Retenție fișiere',
    cost_control: 'Cost control',
    securitate: 'Securitate',
    email: 'Email (SMTP)',
    aplicatie: 'Aplicație',
    liste_optiuni: 'Liste & opțiuni',
    cache_redis: 'Cache Redis',
};
/**
 * Registry-ul complet de setări non-secrete. Extras din spec-uri:
 *  - spec-pipeline §3 (regen_limit default 2), §5 (pronunciation_free_regens default 3)
 *  - spec-pipeline §7 / spec-infra §6 (buget zilnic AI, plafon cost/clip, kill-switch)
 *  - spec-infra §6 / spec-pipeline §8 (retention_months default 6, max upload)
 *  - spec-arhitectura §9 / spec-admin §2 (versiune minimă frontend, nivel logare)
 */
export const SETTINGS_REGISTRY = [
    // --- Limite generare ---
    {
        key: 'generation.free_regens',
        type: 'number',
        category: 'limite_generare',
        label: 'Regenerări gratuite / clip',
        description: 'Câte regenerări de video sunt incluse gratuit per clip înainte de plată suplimentară.',
        default: 2,
        integer: true,
        min: 0,
        max: 20,
    },
    {
        key: 'generation.concurrent_max',
        type: 'number',
        category: 'limite_generare',
        label: 'Generări simultane / utilizator',
        description: 'Numărul maxim de clipuri care se pot genera în paralel pentru un utilizator.',
        default: 3,
        integer: true,
        min: 1,
        max: 50,
    },
    {
        key: 'generation.clips_per_day',
        type: 'number',
        category: 'limite_generare',
        label: 'Clipuri / zi / utilizator',
        description: 'Plafonul zilnic de clipuri generate per utilizator (anti-abuz).',
        default: 10,
        integer: true,
        min: 1,
        max: 500,
    },
    {
        key: 'generation.job_timeout_s',
        type: 'number',
        category: 'limite_generare',
        label: 'Timeout job generare',
        description: 'Durata maximă a unui job de generare înainte de a fi marcat eșuat.',
        default: 600,
        integer: true,
        min: 30,
        max: 7200,
        unit: 's',
    },
    {
        key: 'pronunciation.free_regens',
        type: 'number',
        category: 'limite_generare',
        label: 'Regenerări pronunție gratuite / clip',
        description: 'Câte regenerări de segment pentru corectarea pronunției sunt gratuite per clip.',
        default: 3,
        integer: true,
        min: 0,
        max: 20,
    },
    {
        key: 'tts.elevenlabs_pronunciation_dictionary_id',
        type: 'string',
        category: 'limite_generare',
        label: 'ElevenLabs — ID dicționar pronunție',
        description: 'ID-ul dicționarului de pronunție ElevenLabs (RO/brand). Gol = fără dicționar. Se populează cu `pnpm --filter api seed:pronunciation-dict`.',
        default: '',
    },
    {
        key: 'tts.elevenlabs_pronunciation_dictionary_version_id',
        type: 'string',
        category: 'limite_generare',
        label: 'ElevenLabs — version ID dicționar',
        description: 'Version ID al dicționarului de pronunție (opțional; lipsește = latest pe EL).',
        default: '',
    },
    {
        key: 'generation.max_technical_retries',
        type: 'number',
        category: 'limite_generare',
        label: 'Retry-uri tehnice / pas',
        description: 'Câte reîncercări tehnice (eroare provider) sunt permise per pas înainte de a marca clipul eșuat. Retry-ul tehnic e gratuit și nu consumă regenerările.',
        default: 3,
        integer: true,
        min: 0,
        max: 10,
    },
    {
        key: 'ultra.sla_hours',
        type: 'number',
        category: 'limite_generare',
        label: 'SLA Ultra (livrare editor)',
        description: 'Termenul (în ore) pentru livrarea unui job Ultra de către editor, din care se calculează SLA-ul cu countdown în admin.',
        default: 48,
        integer: true,
        min: 1,
        max: 720,
        unit: 'ore',
    },
    {
        key: 'ultra.included_revisions',
        type: 'number',
        category: 'limite_generare',
        label: 'Revizii Ultra incluse',
        description: 'Câte cereri de revizie sunt incluse gratuit per job Ultra înainte de a atinge limita (REVISION_LIMIT).',
        default: 2,
        integer: true,
        min: 0,
        max: 20,
    },
    {
        key: 'imports.max_page_size_mb',
        type: 'number',
        category: 'limite_generare',
        label: 'Dimensiune maximă pagină import',
        description: 'Mărimea maximă a paginii HTML descărcate la importul unui produs din URL (protecție anti-abuz).',
        default: 5,
        min: 1,
        max: 100,
        unit: 'MB',
    },
    {
        key: 'imports.max_images',
        type: 'number',
        category: 'limite_generare',
        label: 'Imagini importate / produs',
        description: 'Numărul maxim de imagini descărcate în galerie la importul unui produs din URL.',
        default: 6,
        integer: true,
        min: 0,
        max: 20,
    },
    {
        key: 'imports.fetch_timeout_s',
        type: 'number',
        category: 'limite_generare',
        label: 'Timeout fetch import',
        description: 'Durata maximă a unei cereri de fetch la importul din URL (pagină + imagini), înainte de abandon.',
        default: 10,
        integer: true,
        min: 1,
        max: 60,
        unit: 's',
    },
    {
        key: 'imports.max_explore_products',
        type: 'number',
        category: 'limite_generare',
        label: 'Produse max la explorare site',
        description: 'Numărul maxim de produse create când utilizatorul bifează „Explorează site-ul” la importul din URL.',
        default: 20,
        integer: true,
        min: 1,
        max: 50,
    },
    // --- Retenție fișiere ---
    {
        key: 'files.retention_months',
        type: 'number',
        category: 'retentie_fisiere',
        label: 'Retenție fișiere/clipuri',
        description: 'După câte luni de la livrare se șterg fișierele și clipurile (cron + notificare prealabilă).',
        default: 6,
        integer: true,
        min: 1,
        max: 120,
        unit: 'luni',
    },
    {
        key: 'files.max_upload_mb',
        type: 'number',
        category: 'retentie_fisiere',
        label: 'Dimensiune maximă upload',
        description: 'Mărimea maximă acceptată pentru un fișier încărcat prin presigned URL.',
        default: 50,
        integer: true,
        min: 1,
        max: 1024,
        unit: 'MB',
    },
    // --- Cost control ---
    {
        key: 'cost.daily_ai_budget_ron',
        type: 'number',
        category: 'cost_control',
        label: 'Buget zilnic global AI',
        description: 'Bugetul zilnic pentru apeluri AI. La 80% alertă, la 100% generările noi se refuză automat.',
        default: 500,
        min: 0,
        unit: 'RON',
    },
    {
        key: 'cost.cap_per_clip_premium_ron',
        type: 'number',
        category: 'cost_control',
        label: 'Plafon cost / clip (Premium)',
        description: 'Costul maxim AI acceptat pentru un clip Premium înainte de oprire în „depășit".',
        default: 20,
        min: 0,
        unit: 'RON',
    },
    {
        key: 'cost.cap_per_clip_ultra_ron',
        type: 'number',
        category: 'cost_control',
        label: 'Plafon cost / clip (Ultra)',
        description: 'Costul maxim AI acceptat pentru un clip Ultra înainte de oprire în „depășit".',
        default: 60,
        min: 0,
        unit: 'RON',
    },
    {
        key: 'cost.regen_extra_price_ron',
        type: 'number',
        category: 'cost_control',
        label: 'Preț regenerare suplimentară',
        description: 'Prețul unei regenerări peste limita gratuită (încasat prin Stripe).',
        default: 10,
        min: 0,
        unit: 'RON',
    },
    {
        key: 'cost.kill_switch',
        type: 'boolean',
        category: 'cost_control',
        label: 'Kill-switch generări',
        description: 'Oprește instant orice apel nou către provideri (pauză cozi generation + media).',
        default: false,
    },
    {
        key: 'files.retention_warning_days',
        type: 'number',
        category: 'retentie_fisiere',
        label: 'Avertizare prealabilă retenție',
        description: 'Cu câte zile înainte de ștergerea prin retenție se trimite notificarea/email-ul de avertizare (o singură dată per clip).',
        default: 14,
        integer: true,
        min: 1,
        max: 90,
        unit: 'zile',
    },
    // --- Securitate ---
    {
        key: 'ratelimit.public_per_min',
        type: 'number',
        category: 'securitate',
        label: 'Limită cereri publice / minut / IP',
        description: 'Numărul maxim de cereri pe minut, per IP, pe rutele publice (SKU-uri, share, versiune).',
        default: 300,
        integer: true,
        min: 10,
        max: 100000,
    },
    {
        key: 'ratelimit.generation_per_min',
        type: 'number',
        category: 'securitate',
        label: 'Limită generări / minut / utilizator',
        description: 'Numărul maxim de operațiuni de generare/regenerare pe minut, per utilizator (anti-flood pe buget AI).',
        default: 30,
        integer: true,
        min: 1,
        max: 10000,
    },
    {
        key: 'ratelimit.import_per_min',
        type: 'number',
        category: 'securitate',
        label: 'Limită import URL / minut / utilizator',
        description: 'Numărul maxim de importuri de produs din URL pe minut, per utilizator (protecție SSRF/abuz).',
        default: 10,
        integer: true,
        min: 1,
        max: 1000,
    },
    {
        key: 'auth.otp_max_attempts',
        type: 'number',
        category: 'securitate',
        label: 'Încercări OTP eșuate până la lockout',
        description: 'Câte încercări de autentificare OTP eșuate, per IP, până la blocarea temporară (429 cu Retry-After).',
        default: 5,
        integer: true,
        min: 3,
        max: 50,
    },
    {
        key: 'auth.otp_lockout_window_s',
        type: 'number',
        category: 'securitate',
        label: 'Fereastră lockout OTP',
        description: 'Durata (în secunde) a ferestrei în care se numără încercările OTP eșuate și cât ține blocarea.',
        default: 900,
        integer: true,
        min: 30,
        max: 86400,
        unit: 's',
    },
    // --- Email (SMTP) ---
    {
        key: 'smtp.enabled',
        type: 'boolean',
        category: 'email',
        label: 'Trimitere email prin SMTP',
        description: 'Când e activ și configurat (host + parolă), email-urile se trimit prin SMTP real. Altfel se folosește transportul mock (loghează, nu trimite).',
        default: false,
    },
    {
        key: 'smtp.host',
        type: 'string',
        category: 'email',
        label: 'Server SMTP (host)',
        description: 'Hostul serverului SMTP (ex. smtp.eu.mailgun.org). Parola se setează separat, criptat (write-only).',
        default: '',
    },
    {
        key: 'smtp.port',
        type: 'number',
        category: 'email',
        label: 'Port SMTP',
        description: 'Portul serverului SMTP (587 STARTTLS, 465 TLS implicit).',
        default: 587,
        integer: true,
        min: 1,
        max: 65535,
    },
    {
        key: 'smtp.secure',
        type: 'boolean',
        category: 'email',
        label: 'TLS implicit (port 465)',
        description: 'Activează conexiunea TLS de la început (port 465). Pentru 587 (STARTTLS) lasă dezactivat.',
        default: false,
    },
    {
        key: 'smtp.user',
        type: 'string',
        category: 'email',
        label: 'Utilizator SMTP',
        description: 'Numele de utilizator pentru autentificarea SMTP.',
        default: '',
    },
    {
        key: 'smtp.from',
        type: 'string',
        category: 'email',
        label: 'Expeditor (From)',
        description: 'Adresa/afișarea expeditorului pentru email-urile trimise clienților.',
        default: 'Reclame Video <no-reply@reclamevideo.ro>',
    },
    // --- Aplicație ---
    {
        key: 'app.min_frontend_version',
        type: 'string',
        category: 'aplicatie',
        label: 'Versiune minimă frontend',
        description: 'Sub această versiune, clienții primesc reload forțat („Versiune nouă — Reîncarcă").',
        default: '0.0.0',
        pattern: '^\\d+\\.\\d+\\.\\d+$',
    },
    {
        key: 'app.log_level',
        type: 'string',
        category: 'aplicatie',
        label: 'Nivel logare',
        description: 'Nivelul de detaliu al logurilor structurate (pino).',
        default: 'info',
        enum: ['debug', 'info', 'warn', 'error'],
    },
    {
        key: 'app.maintenance_mode',
        type: 'boolean',
        category: 'aplicatie',
        label: 'Mod mentenanță',
        description: 'Când e activ, aplicația afișează un mesaj de mentenanță și blochează operațiunile.',
        default: false,
    },
    {
        key: 'orders.pending_expiry_hours',
        type: 'number',
        category: 'aplicatie',
        label: 'Expirare comandă neplătită',
        description: 'După câte ore o comandă rămasă „pending" (fără plată confirmată) este marcată automat „expired" (cron).',
        default: 24,
        integer: true,
        min: 1,
        max: 168,
        unit: 'ore',
    },
    // --- Liste & opțiuni (definibile din admin, citite de fronturi) ---
    {
        key: 'options.providers_by_step',
        type: 'json',
        jsonShape: 'map_string_list',
        category: 'liste_optiuni',
        label: 'Provideri disponibili per tip de pas',
        description: 'Ce provideri poți alege la fiecare tip de pas din workflow. Format: obiect { tip_pas: ["provider1","provider2"] }. Chei: orchestrator, image_generate, image_edit, image_to_video, tts, voice_change. Providerii trebuie să fie unii pe care codul îi rezolvă (openai/anthropic/gemini/bfl/grok/elevenlabs).',
        default: DEFAULT_PROVIDERS_BY_STEP,
    },
    {
        key: 'options.models_by_provider',
        type: 'json',
        jsonShape: 'map_option_list',
        category: 'liste_optiuni',
        label: 'Modele per provider',
        description: 'Modelele disponibile pentru fiecare provider, cu label prietenos. Format: obiect { provider: [{"value":"gpt-5.6-luna","label":"GPT-4o"}] }. Câmpul model din editorul de pași e „creatable" — poți tasta oricând un model nelistat.',
        default: DEFAULT_MODELS_BY_PROVIDER,
    },
    {
        key: 'options.catalog_badges',
        type: 'json',
        jsonShape: 'option_list',
        category: 'liste_optiuni',
        label: 'Badge-uri catalog (SKU)',
        description: 'Badge-urile sugerate pentru SKU-uri în catalog. Format: listă [{"value":"Recomandat","label":"Recomandat"}]. Câmpul badge din catalog e „creatable" — poți tasta un badge nou.',
        default: DEFAULT_CATALOG_BADGES,
    },
    {
        key: 'options.template_niches',
        type: 'json',
        jsonShape: 'string_list',
        category: 'liste_optiuni',
        label: 'Nișe șabloane',
        description: 'Sugestiile de nișă la crearea/editarea unui șablon. Format: listă de texte ["HoReCa","eCommerce"]. Câmpul e „creatable".',
        default: DEFAULT_TEMPLATE_NICHES,
    },
    {
        key: 'options.music_moods',
        type: 'json',
        jsonShape: 'string_list',
        category: 'liste_optiuni',
        label: 'Mood-uri muzică',
        description: 'Sugestiile de mood la adăugarea unei piese muzicale. Format: listă de texte ["energic","calm"]. Câmpul e „creatable".',
        default: DEFAULT_MUSIC_MOODS,
    },
    {
        key: 'options.voice_styles',
        type: 'json',
        jsonShape: 'string_list',
        category: 'liste_optiuni',
        label: 'Stiluri voce',
        description: 'Sugestiile de stil la adăugarea/editarea unei voci. Format: listă de texte ["energic","profesional"]. Câmpul e „creatable".',
        default: DEFAULT_VOICE_STYLES,
    },
    // --- Cache Redis ---
    {
        key: 'cache.settings_ttl_s',
        type: 'number',
        category: 'cache_redis',
        label: 'TTL cache setări',
        description: 'Durata de viață a valorilor de setări în cache-ul Redis înainte de recitire din Postgres.',
        default: 300,
        integer: true,
        min: 5,
        max: 86400,
        unit: 's',
    },
];
const REGISTRY_BY_KEY = new Map(SETTINGS_REGISTRY.map((setting) => [setting.key, setting]));
export function findSetting(key) {
    return REGISTRY_BY_KEY.get(key);
}
export function settingsByCategory() {
    const grouped = Object.fromEntries(Object.keys(SETTING_CATEGORIES).map((c) => [c, []]));
    for (const setting of SETTINGS_REGISTRY) {
        grouped[setting.category].push(setting);
    }
    return grouped;
}
/**
 * Validează (și coerce) o valoare brută pentru o setare din registry.
 * Respinge tipuri greșite și încălcări de constrângeri (min/max/enum/pattern).
 */
export function validateSettingValue(def, raw) {
    switch (def.type) {
        case 'boolean': {
            if (typeof raw === 'boolean')
                return { ok: true, value: raw };
            if (raw === 'true' || raw === 'false')
                return { ok: true, value: raw === 'true' };
            return { ok: false, error: `„${def.key}" trebuie să fie boolean.` };
        }
        case 'number': {
            const n = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
            if (typeof n !== 'number' || Number.isNaN(n) || !Number.isFinite(n)) {
                return { ok: false, error: `„${def.key}" trebuie să fie un număr.` };
            }
            if (def.integer && !Number.isInteger(n)) {
                return { ok: false, error: `„${def.key}" trebuie să fie un număr întreg.` };
            }
            if (def.min !== undefined && n < def.min) {
                return { ok: false, error: `„${def.key}" trebuie să fie ≥ ${def.min}.` };
            }
            if (def.max !== undefined && n > def.max) {
                return { ok: false, error: `„${def.key}" trebuie să fie ≤ ${def.max}.` };
            }
            if (def.enum && !def.enum.includes(n)) {
                return { ok: false, error: `„${def.key}" trebuie să fie una din: ${def.enum.join(', ')}.` };
            }
            return { ok: true, value: n };
        }
        case 'string': {
            if (typeof raw !== 'string') {
                return { ok: false, error: `„${def.key}" trebuie să fie text.` };
            }
            if (def.enum && !def.enum.includes(raw)) {
                return { ok: false, error: `„${def.key}" trebuie să fie una din: ${def.enum.join(', ')}.` };
            }
            if (def.pattern && !new RegExp(def.pattern).test(raw)) {
                return { ok: false, error: `„${def.key}" nu respectă formatul cerut.` };
            }
            return { ok: true, value: raw };
        }
        case 'json': {
            // Acceptă și string JSON (îl parsează) sau obiect/array deja parsat.
            let parsed = raw;
            if (typeof raw === 'string') {
                try {
                    parsed = JSON.parse(raw);
                }
                catch {
                    return { ok: false, error: `„${def.key}" nu este JSON valid (nu se poate parsa).` };
                }
            }
            if (def.jsonShape) {
                return validateJsonShape(def.key, def.jsonShape, parsed);
            }
            if (parsed === null || typeof parsed !== 'object') {
                return { ok: false, error: `„${def.key}" trebuie să fie un obiect JSON.` };
            }
            return { ok: true, value: parsed };
        }
        default:
            return { ok: false, error: `Tip de setare necunoscut pentru „${def.key}".` };
    }
}
/** True pentru `{ value: string; label: string; description?: string }`. */
function isOption(v) {
    if (v === null || typeof v !== 'object' || Array.isArray(v))
        return false;
    const o = v;
    if (typeof o.value !== 'string' || o.value.trim() === '')
        return false;
    if (typeof o.label !== 'string' || o.label.trim() === '')
        return false;
    if (o.description !== undefined && typeof o.description !== 'string')
        return false;
    return true;
}
function isStringList(v) {
    return Array.isArray(v) && v.every((x) => typeof x === 'string' && x.trim() !== '');
}
function isOptionList(v) {
    return Array.isArray(v) && v.every(isOption);
}
/** Validează un obiect JSON după forma declarată (`jsonShape`). */
export function validateJsonShape(key, shape, parsed) {
    const err = (msg) => ({ ok: false, error: `„${key}": ${msg}` });
    switch (shape) {
        case 'string_list':
            return isStringList(parsed)
                ? { ok: true, value: parsed }
                : err('trebuie să fie o listă de texte (ex. ["a","b"]).');
        case 'option_list':
            return isOptionList(parsed)
                ? { ok: true, value: parsed }
                : err('trebuie să fie o listă de {value,label} (ex. [{"value":"x","label":"X"}]).');
        case 'map_string_list': {
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                return err('trebuie să fie un obiect { cheie: ["a","b"] }.');
            }
            for (const [k, v] of Object.entries(parsed)) {
                if (!isStringList(v))
                    return err(`valoarea pentru „${k}" trebuie să fie o listă de texte.`);
            }
            return { ok: true, value: parsed };
        }
        case 'map_option_list': {
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                return err('trebuie să fie un obiect { cheie: [{value,label}] }.');
            }
            for (const [k, v] of Object.entries(parsed)) {
                if (!isOptionList(v))
                    return err(`valoarea pentru „${k}" trebuie să fie o listă de {value,label}.`);
            }
            return { ok: true, value: parsed };
        }
        default:
            return err('formă JSON necunoscută.');
    }
}
//# sourceMappingURL=settings-registry.js.map